import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { cn } from '@/lib/utils';
import {
  Cake,
  Stethoscope,
  Clock,
  LogIn,
  LogOut,
  MapPin,
  Navigation,
  CheckCircle2,
  AlertTriangle,
  Phone
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import i18n from '@/i18n';

interface ActiveShift {
  id: string;
  check_in_at: string;
  caregiver_name?: string | null;
  caregiver_phone?: string | null;
  check_in_latitude?: number | null;
  check_in_longitude?: number | null;
}

interface PatientData {
  id: string;
  nickname: string;
  photo_url?: string | null;
  birth_date?: string | null;
  health_details?: string | null;
  notes?: string | null;
  address?: string | null;
  residence_latitude?: number | null;
  residence_longitude?: number | null;
}

interface PatientDetailsCardProps {
  patient: PatientData | null;
  activeShift?: ActiveShift | null;
  showCheckIn?: boolean;
  showCheckOut?: boolean;
  isCheckedIn?: boolean;
  onCheckIn?: () => void;
  onCheckOut?: () => void;
  showHealthDetails?: boolean;
  showNotes?: boolean;
  showLocationStatus?: boolean;
  showCaregiverPhone?: boolean;
  allowedRadius?: number; // in meters, default 500
  onClick?: () => void;
  showEditHint?: boolean;
  onForceCheckOut?: () => void;
  isForceCheckingOut?: boolean;
  activeShiftInOtherPatient?: {
    patientId: string;
    patientName?: string;
    checkInAt: string;
  } | null;
  onGoToActiveShift?: () => void;
}

// Haversine formula to calculate distance between two coordinates in meters
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

export function PatientDetailsCard({
  patient,
  activeShift,
  showCheckIn = false,
  showCheckOut = false,
  isCheckedIn = false,
  onCheckIn,
  onCheckOut,
  showHealthDetails = true,
  showNotes = true,
  showLocationStatus = false,
  showCaregiverPhone = false,
  allowedRadius = 500,
  onClick,
  showEditHint = false,
  onForceCheckOut,
  isForceCheckingOut = false,
  activeShiftInOtherPatient,
  onGoToActiveShift
}: PatientDetailsCardProps) {
  const { t } = useTranslation();
  const dateLocale = i18n.language === 'pt-BR' ? ptBR : enUS;

  if (!patient) return null;

  const calculateAge = (birthDate: string) => {
    const birth = new Date(birthDate + 'T00:00:00');
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Calculate distance from residence if location status is enabled
  const getDistanceInfo = () => {
    if (!showLocationStatus || !activeShift) return null;
    if (!activeShift.check_in_latitude || !activeShift.check_in_longitude) return null;
    if (!patient.residence_latitude || !patient.residence_longitude) return null;

    const distance = calculateDistance(
      activeShift.check_in_latitude,
      activeShift.check_in_longitude,
      patient.residence_latitude,
      patient.residence_longitude
    );

    return {
      distance,
      formatted: formatDistance(distance),
      isWithinRadius: distance <= allowedRadius
    };
  };

  const distanceInfo = getDistanceInfo();

  return (
    <Card
      className={cn(onClick && "cursor-pointer hover:bg-accent/50 transition-colors")}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <SignedAvatar
            src={patient.photo_url}
            bucket="patient-photos"
            className="w-16 h-16"
            fallback={
              <span className="bg-primary/10 text-primary text-xl w-full h-full flex items-center justify-center rounded-full">
                {patient.nickname?.charAt(0)}
              </span>
            }
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold">{patient.nickname}</h1>

                {/* Active Shift Status & Force Checkout - Now in Header */}
                <div className="flex flex-col gap-2 mt-1">
                  {activeShift ? (
                    <div className="flex items-center flex-wrap gap-2">
                      <Badge variant="default">
                        <Clock className="w-3 h-3 mr-1" />
                        {activeShift.caregiver_name
                          ? t('shift.caregiverInShift', { name: activeShift.caregiver_name })
                          : t('shift.activeShift')
                        }
                      </Badge>

                      {/* Shift start time display for everyone */}
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t('shift.startedAtLabel')} {format(new Date(activeShift.check_in_at), 'HH:mm')}
                      </span>

                      {/* Force Layout Button - Top Rightish */}
                      {onForceCheckOut && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onForceCheckOut();
                          }}
                          disabled={isForceCheckingOut}
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {isForceCheckingOut ? t('common.loading') : t('caregiver.forceCheckOut')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Badge variant="secondary" className="w-fit">
                      {t('shift.noActiveShift')}
                    </Badge>
                  )}

                  {/* Call caregiver CTA - for owners and observers */}
                  {showCaregiverPhone && activeShift?.caregiver_phone && (
                    <a
                      href={`tel:${activeShift.caregiver_phone.replace(/[^+\d]/g, '')}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline w-fit"
                      aria-label={t('shift.callCaregiver', { defaultValue: 'Ligar para o cuidador' })}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>{activeShift.caregiver_phone}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Birth date */}
        {patient.birth_date && (
          <div className="space-y-1 mt-3">
            <div className="flex items-center gap-2 text-sm">
              <Cake className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">{t('patient.birthDate')}:</span>
              <span>{format(new Date(patient.birth_date + 'T00:00:00'), 'dd/MM/yyyy')}</span>
            </div>
            <p className="text-sm text-muted-foreground ml-6">
              {calculateAge(patient.birth_date)} anos
            </p>
          </div>
        )}

        {/* Address */}
        {patient.address && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="truncate">{patient.address}</span>
          </div>
        )}

        {showEditHint && (
          <p className="text-xs text-primary mt-2">{t('common.tapToEdit')}</p>
        )}


        {/* Active shift in other patient warning */}
        {activeShiftInOtherPatient && (
          <div className="flex items-start gap-3 p-3 rounded-lg mt-3 bg-warning/10 border border-warning/30">
            <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-warning">
                {t('shift.activeInOtherPatient')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activeShiftInOtherPatient.patientName && (
                  <span>{activeShiftInOtherPatient.patientName} · </span>
                )}
                {t('shift.checkedInAt')} {format(new Date(activeShiftInOtherPatient.checkInAt), 'HH:mm', { locale: dateLocale })}
              </p>
              <Button
                variant="outline"
                size="default"
                className="mt-2 border-warning/50 text-warning hover:bg-warning/10"
                onClick={(e) => {
                  e.stopPropagation();
                  onGoToActiveShift?.();
                }}
              >
                <Navigation className="w-4 h-4 mr-2" />
                {t('shift.goToActiveShift')}
              </Button>
            </div>
          </div>
        )}

        {/* Location Status Indicator */}
        {showLocationStatus && activeShift && distanceInfo && (
          <div className={`flex items-center gap-2 p-2 rounded-lg mt-3 ${distanceInfo.isWithinRadius
            ? 'bg-success/10 text-success'
            : 'bg-warning/10 text-warning'
            }`}>
            {distanceInfo.isWithinRadius ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
            <Navigation className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('shift.checkInDistance', { distance: distanceInfo.formatted })}
              {distanceInfo.isWithinRadius
                ? ` ${t('shift.withinAllowedRadius')}`
                : ` ${t('shift.outsideAllowedRadius', { radius: allowedRadius })}`
              }
            </span>
          </div>
        )}

        {/* Health Details */}
        {showHealthDetails && patient.health_details && (
          <div className="p-3 bg-muted/50 rounded-lg mt-4">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-1">
              <Stethoscope className="w-3 h-3" />
              {t('patient.healthDetails')}
            </div>
            <p className="text-sm">
              {patient.health_details}
            </p>
          </div>
        )}

        {/* Notes */}
        {showNotes && patient.notes && (
          <div className="p-3 bg-muted/50 rounded-lg mt-2">
            <p className="text-xs font-medium text-muted-foreground mb-1">{t('patient.observations')}</p>
            <p className="text-sm">{patient.notes}</p>
          </div>
        )}

        {/* Action Buttons - Bottom Full Width */}
        {(showCheckIn || showCheckOut) && (
          <div className="mt-4 pt-4 border-t border-border">
            {showCheckOut && isCheckedIn && onCheckOut && (
              <Button
                variant="outline"
                className="w-full"
                size="lg"
                onClick={onCheckOut}
              >
                <LogOut className="w-5 h-5 mr-2" />
                {t('shift.checkOutShort')}
              </Button>
            )}
            {showCheckIn && !isCheckedIn && onCheckIn && (
              <Button
                className="w-full"
                size="lg"
                onClick={onCheckIn}
              >
                <LogIn className="w-5 h-5 mr-2" />
                {t('shift.checkInShort')}
              </Button>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  );
}
